<template>
  <div class="emailStats">
    <div v-if="loading" class="muted">Loading email stats…</div>
    <div v-else-if="error" class="errorText">{{ error }}</div>
    <template v-else-if="data">
      <!-- Summary chips -->
      <div class="summaryRow">
        <div class="summaryChip">
          <span class="chipVal">{{ data.total_sent }}</span>
          <span class="chipLabel">Sent</span>
        </div>
        <div class="summaryChip chipBounce">
          <span class="chipVal">{{ data.total_bounced }}</span>
          <span class="chipLabel">Bounced</span>
          <span v-if="data.total_sent > 0" class="chipPct">({{ pct(data.total_bounced, data.total_sent) }})</span>
        </div>
        <div class="summaryChip chipReply">
          <span class="chipVal">{{ data.total_replied }}</span>
          <span class="chipLabel">Replies</span>
          <span v-if="data.total_sent > 0" class="chipPct">({{ pct(data.total_replied, data.total_sent) }})</span>
        </div>
      </div>

      <!-- Per-address table -->
      <div v-if="data.addresses.length > 0" class="addrTable">
        <div class="addrHeader">
          <div class="col-email">Email Address</div>
          <div class="col-contact">Contact</div>
          <div class="col-account">Account</div>
          <div class="col-num">Sent</div>
          <div class="col-num">Bounced</div>
          <div class="col-num">Replies</div>
        </div>
        <div
          v-for="addr in visibleAddresses"
          :key="addr.email"
          class="addrRow"
        >
          <div class="col-email addrEmail">{{ addr.email }}</div>
          <div class="col-contact addrMeta">{{ addr.contact_name || '—' }}</div>
          <div class="col-account addrMeta">
            <FavIcon v-if="addr.account_domain" :domain="addr.account_domain" :size="13" class="addrFavicon" />
            <span>{{ addr.account_name || '—' }}</span>
          </div>
          <div class="col-num">{{ addr.sent_count }}</div>
          <div class="col-num" :class="{ danger: addr.bounce_count > 0 }">{{ addr.bounce_count }}</div>
          <div class="col-num" :class="{ success: addr.reply_count > 0 }">{{ addr.reply_count }}</div>
        </div>
      </div>
      <div v-else class="muted">No sent emails found for this sequence.</div>

      <button v-if="data.addresses.length > SHOW_LIMIT && !showAll" class="btn showMoreBtn" @click="showAll = true">
        Show all {{ data.addresses.length }} addresses
      </button>
    </template>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import FavIcon from './FavIcon.vue'
import { apiFetch } from '../services/api.ts'

const props = defineProps({
  sequenceId: { type: String, required: true },
  pollTick: { type: Number, default: 0 },
})

const SHOW_LIMIT = 10

const loading = ref(false)
const error = ref('')
const data = ref(null)
const showAll = ref(false)

const visibleAddresses = computed(() => {
  if (!data.value) return []
  if (showAll.value) return data.value.addresses
  return data.value.addresses.slice(0, SHOW_LIMIT)
})

function pct(n, total) {
  if (!total) return '0%'
  return Math.round((n / total) * 100) + '%'
}

async function load() {
  if (!props.sequenceId) return
  loading.value = true
  error.value = ''
  try {
    const resp = await apiFetch(`/sequences/${encodeURIComponent(props.sequenceId)}/email-stats`)
    data.value = resp
  } catch (e) {
    error.value = e?.message || 'Failed to load email stats'
  } finally {
    loading.value = false
  }
}

async function silentReload() {
  if (!props.sequenceId) return
  try {
    const resp = await apiFetch(`/sequences/${encodeURIComponent(props.sequenceId)}/email-stats`)
    data.value = resp
  } catch {
    // silent — don't disrupt display on poll failure
  }
}

onMounted(load)
watch(() => props.sequenceId, load)
watch(() => props.pollTick, silentReload)
</script>

<style scoped>
.muted { color: var(--muted); font-size: 13px; }
.errorText { color: var(--danger); font-size: 13px; }

.summaryRow {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 16px;
}

.summaryChip {
  display: flex;
  align-items: baseline;
  gap: 5px;
  padding: 6px 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--surface);
}

.chipVal {
  font-size: 18px;
  font-weight: 700;
  color: var(--text);
}

.chipLabel {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
}

.chipPct {
  font-size: 12px;
  color: var(--muted);
}

.chipBounce .chipVal { color: var(--danger); }
.chipReply .chipVal { color: var(--success); }

.addrTable {
  border: 1px solid var(--border);
  border-radius: 8px;
  overflow: hidden;
}

.addrHeader {
  display: grid;
  grid-template-columns: minmax(180px,2fr) minmax(110px,1fr) minmax(120px,1.2fr) 50px 70px 60px;
  gap: 10px;
  padding: 7px 14px;
  background: var(--surface2, var(--surface));
  border-bottom: 1px solid var(--border);
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--muted);
}

.addrRow {
  display: grid;
  grid-template-columns: minmax(180px,2fr) minmax(110px,1fr) minmax(120px,1.2fr) 50px 70px 60px;
  gap: 10px;
  padding: 9px 14px;
  border-bottom: 1px solid var(--border2);
  font-size: 13px;
  align-items: center;
}

.addrRow:last-child {
  border-bottom: none;
}

.addrEmail {
  font-weight: 500;
  color: var(--text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.addrMeta {
  display: flex;
  align-items: center;
  gap: 5px;
  color: var(--muted);
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.addrFavicon { border-radius: 2px; }

.col-num {
  font-size: 13px;
  font-weight: 600;
  color: var(--text);
  text-align: right;
}

.col-num.danger { color: var(--danger); }
.col-num.success { color: var(--success); }

.showMoreBtn {
  margin-top: 10px;
  font-size: 12px;
}
</style>
